import { useState } from 'react';
import { Copy, Check, Download, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import CodeRunner from './CodeRunner';

const CodeBlock = ({ code, language, filename }) => {
  const { language: lang } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [showRunner, setShowRunner] = useState(false);

  const txt = {
    es: { copy: 'Copiar', copied: 'Copiado', download: 'Descargar', code: 'código', run: 'Ejecutar' },
    en: { copy: 'Copy', copied: 'Copied', download: 'Download', code: 'code', run: 'Run' }
  };
  const t = txt[lang] || txt.es;

  // Check if language is executable
  const executableLangs = ['python', 'python3', 'py', 'javascript', 'js', 'node', 'nodejs'];
  const isExecutable = executableLangs.includes(language?.toLowerCase());

  const handleCopy = async () => {
    // Helper function for textarea fallback
    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    // Try modern clipboard API first
    let success = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(code);
        success = true;
      }
    } catch (error) {
    }

    // Fallback to execCommand
    if (!success) {
      success = copyWithTextarea();
    }

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const extension = getExtension(language);
    const name = filename || `code.${extension}`;
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getExtension = (lang) => {
    const extensions = {
      javascript: 'js', typescript: 'ts', python: 'py', html: 'html',
      css: 'css', json: 'json', jsx: 'jsx', tsx: 'tsx', sql: 'sql',
      bash: 'sh', shell: 'sh', yaml: 'yaml', yml: 'yml', markdown: 'md',
      java: 'java', cpp: 'cpp', c: 'c', go: 'go', rust: 'rs',
      php: 'php', ruby: 'rb', swift: 'swift', kotlin: 'kt',
    };
    return extensions[lang?.toLowerCase()] || 'txt';
  };

  return (
    <div className="my-4 rounded-xl overflow-hidden border border-zinc-700/50 light:border-zinc-300 bg-zinc-900 light:bg-zinc-100">
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-800 light:bg-zinc-200 border-b border-zinc-700/50 light:border-zinc-300">
        <span className="text-xs font-mono text-zinc-400 light:text-zinc-600">
          {language || t.code}
        </span>
        <div className="flex gap-2">
          {isExecutable && (
            <button
              onClick={() => setShowRunner(!showRunner)}
              className={`flex items-center gap-1 px-2 py-1 text-xs transition-colors ${
                showRunner 
                  ? 'text-green-400 bg-green-600/20' 
                  : 'text-green-400 hover:text-green-300'
              } rounded`}
              data-testid="run-code-btn"
            >
              <Play className="w-3.5 h-3.5" />
              {t.run}
            </button>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200 light:text-zinc-600 light:hover:text-zinc-800 transition-colors"
            data-testid="copy-code-btn"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? t.copied : t.copy}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-400 hover:text-indigo-300 light:text-indigo-600 light:hover:text-indigo-500 transition-colors"
            data-testid="download-code-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {t.download}
          </button>
        </div>
      </div>
      <pre className="p-4 overflow-x-auto text-sm">
        <code className="text-zinc-300 light:text-zinc-700 font-mono whitespace-pre">
          {code}
        </code>
      </pre>
      
      {/* Code Runner */}
      {showRunner && isExecutable && (
        <CodeRunner 
          code={code} 
          language={language} 
          onClose={() => setShowRunner(false)} 
        />
      )}
    </div>
  );
};

export default CodeBlock;
